const FreddinTitleQuery = {
  words(value) {
    return value.normalize('NFKC').toLowerCase().match(/[\p{L}\p{N}]+/gu) || [];
  },

  matches(lexeme, value) {
    const words = this.words(lexeme);
    const needle = this.words(value).join('');
    return Boolean(needle) && words.some((_, i) => words.slice(i).join('').startsWith(needle));
  },

  vocabulary(titles) {
    return [...new Set(titles.flatMap((title) => title.split(/\s+/u))
      .map((term) => term.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').toLowerCase())
      .filter(Boolean))].sort();
  },

  encode(value, vocabulary) {
    return value.trim().split(/\s+/u).filter(Boolean).map((term) => {
      const alternatives = [term, ...vocabulary.filter((lexeme) =>
        lexeme.toLowerCase() !== term.toLowerCase() && this.matches(lexeme, term))];
      const clause = alternatives.map((word) => 'title:' + JSON.stringify(word)).join(' OR ');
      return alternatives.length > 1 ? '(' + clause + ')' : clause;
    }).join(' AND ');
  },

  decode(value) {
    const term = String.raw`title:"(?:[^"\\]|\\.)*"`;
    const clause = '(?:' + term + '|\\(' + term + '(?: OR ' + term + ')+\\))';
    if (!new RegExp('^' + clause + '(?: AND ' + clause + ')*$', 'u').test(value)) return value;
    return value.split(' AND ').map((part) =>
      JSON.parse(part.match(/^\(?title:("(?:[^"\\]|\\.)*")/u)[1])).join(' ');
  },

  titleMatches(title, value) {
    const lexemes = title.split(/\s+/u);
    return value.trim().split(/\s+/u).filter(Boolean)
      .every((term) => lexemes.some((lexeme) => this.matches(lexeme, term)));
  },
};

const FreddinTitleSearch = {
  vocabulary: null,
  expires: 0,
  pending: null,
  userEdited: false,

  ready() {
    return this.vocabulary !== null && this.expires > Date.now();
  },

  key() {
    return ['freddin-title-index-v1', window.Shopify?.routes?.root || '/',
      window.Shopify?.country || '', window.Shopify?.currency?.active || ''].join(':');
  },

  async load() {
    if (this.ready()) return;
    if (this.pending) return this.pending;
    try {
      const cached = JSON.parse(sessionStorage.getItem(this.key()) || 'null');
      if (cached?.expires > Date.now() && Array.isArray(cached.words) &&
          cached.words.every((word) => typeof word === 'string')) {
        this.vocabulary = cached.words;
        this.expires = cached.expires;
        return;
      }
    } catch {}
    this.pending = (async () => {
      const products = new Map();
      let pages = 1;
      let count = 0;
      for (let page = 1; page <= pages; page++) {
        const root = window.Shopify?.routes?.root || '/';
        const url = new URL(root + 'search', window.location.origin);
        url.searchParams.set('q', '*');
        url.searchParams.set('type', 'product');
        url.searchParams.set('section_id', 'freddin-search-index');
        url.searchParams.set('page', String(page));
        const response = await fetch(url, { signal: AbortSignal.timeout(10000) });
        if (!response.ok) throw new Error('Title index request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const element = html.querySelector('[data-freddin-title-index]');
        if (!element) throw new Error('Title index is unavailable');
        const data = JSON.parse(element.textContent);
        if (!Array.isArray(data.products) || data.products.length > 250 || !Number.isInteger(data.pages) ||
            !Number.isInteger(data.count) || data.count < 0 || data.pages < 0 || data.pages > 100 ||
            (data.count > 0 && data.pages !== Math.ceil(data.count / 250)))
          throw new Error('Invalid title index');
        if (page === 1) {
          pages = data.pages;
          count = data.count;
        } else if (pages !== data.pages || count !== data.count) {
          throw new Error('Title index changed during loading');
        }
        data.products.forEach((product) => {
          if (typeof product.title !== 'string' || !product.id) throw new Error('Invalid title index product');
          products.set(String(product.id), product.title);
        });
      }
      if (products.size !== count) throw new Error('Incomplete title index');
      this.vocabulary = FreddinTitleQuery.vocabulary([...products.values()]);
      this.expires = Date.now() + 60000;
      try {
        sessionStorage.setItem(this.key(), JSON.stringify({ words: this.vocabulary, expires: this.expires }));
      } catch {}
    })();
    try {
      await this.pending;
    } finally {
      this.pending = null;
    }
  },

  encode(value) {
    if (!this.vocabulary) throw new Error('Title index is not loaded');
    return FreddinTitleQuery.encode(value, this.vocabulary);
  },

  decode(value) {
    return FreddinTitleQuery.decode(value);
  },

  canonicalURL(value, action) {
    const url = new URL(value);
    if (url.pathname !== new URL(action, url).pathname || !url.searchParams.get('q')?.trim()) return null;
    const query = this.encode(this.decode(url.searchParams.get('q')));
    if (url.searchParams.get('q') === query && url.searchParams.get('type') === 'product') return null;
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    return url;
  },

  showError(input) {
    let error = input.form.querySelector('[data-freddin-search-error]');
    if (!error) {
      error = document.createElement('p');
      error.setAttribute('data-freddin-search-error', '');
      error.setAttribute('role', 'alert');
      input.form.append(error);
    }
    error.textContent = input.dataset.freddinError;
  },
};

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.value = FreddinTitleSearch.decode(this.input.value);
      this.input.addEventListener('focus', () => FreddinTitleSearch.load().catch(() => {}));
      this.input.addEventListener('input', () => {
        FreddinTitleSearch.userEdited = true;
        this.input.form.querySelector('[data-freddin-search-error]')?.remove();
      });
      if (!this.input.form.freddinTitleSearchBound) {
        this.input.form.freddinTitleSearchBound = true;
        this.input.form.addEventListener('submit', async (event) => {
          FreddinTitleSearch.userEdited = true;
          this.input.form.querySelector('[data-freddin-search-error]')?.remove();
          if (event.defaultPrevented || !this.input.value.trim() ||
              this.input.form.querySelector('[aria-selected="true"] a') || FreddinTitleSearch.ready()) return;
          event.preventDefault();
          this.input.form.freddinRequestedValue = this.input.value;
          this.input.form.freddinSubmitter = event.submitter;
          if (this.input.form.freddinSubmitPending) return;
          this.input.form.freddinSubmitPending = true;
          try {
            await FreddinTitleSearch.load();
            if (this.input.isConnected && this.input.value.trim() &&
                this.input.value === this.input.form.freddinRequestedValue)
              this.input.form.requestSubmit(this.input.form.freddinSubmitter || undefined);
          } catch {
            FreddinTitleSearch.showError(this.input);
          } finally {
            this.input.form.freddinSubmitPending = false;
          }
        });
        this.input.form.addEventListener('formdata', (event) => {
          event.formData.set('q', FreddinTitleSearch.encode(this.input.value));
          event.formData.set('type', 'product');
        });
      }
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.input.form.querySelector('[data-freddin-search-error]')?.remove();
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      FreddinTitleSearch.userEdited = true;
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);


const freddinSearchForm = document.querySelector('form[role="search"]');
if (freddinSearchForm && new URL(window.location.href).pathname === new URL(freddinSearchForm.action).pathname &&
    new URL(window.location.href).searchParams.get('q')?.trim()) {
  const initialURL = new URL(window.location.href);
  const initialQuery = initialURL.searchParams.get('q');
  const encoded = FreddinTitleSearch.decode(initialQuery) !== initialQuery;
  if (encoded) document.title = document.title.replace(initialQuery, FreddinTitleSearch.decode(initialQuery));
  const pendingResults = encoded ? [] : Array.from(document.querySelectorAll(
    '#ProductGridContainer, .template-search .product-count, .template-search .product-count-vertical, .template-search__header > p[role="status"]'
  ));
  pendingResults.forEach((element) => { element.hidden = true; });
  if (!encoded || initialURL.searchParams.get('type') !== 'product') {
  FreddinTitleSearch.load().then(() => {
    if (FreddinTitleSearch.userEdited || window.location.href !== initialURL.href) return;
    const url = FreddinTitleSearch.canonicalURL(window.location.href, freddinSearchForm.action);
    if (url) window.location.replace(url.href);
    else pendingResults.forEach((element) => { element.hidden = false; });
  }).catch(() => FreddinTitleSearch.showError(freddinSearchForm.querySelector('input[type="search"]')));
  }
}
